export type GuidedTourId =
  | 'chats'
  | 'whatsapp'
  | 'company'
  | 'users'
  | 'contacts'
  | 'templates'
  | 'info-center';

export type GuidedTourStepDef = {
  /** מזהה פנימי לדיבוג */
  id: string;
  /** נתיב React Router (ללא query) */
  path: string;
  search?: string;
  /** סלקטור אלמנט — data-tour="..." */
  target: string;
  title: string;
  content: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center' | 'auto';
  /** מרווח סביב ה-spotlight — ערכים קטנים מפחיתים קפיצות בפריסה */
  spotlightPadding?: number;
  /** Tooltip במצב fixed — עוזר כשהיעד בתוך אזור גלילה (למשל חלון צ'אט) */
  joyrideFixed?: boolean;
};

export type GuidedTourTopicDef = {
  id: GuidedTourId;
  title: string;
  description: string;
  steps: GuidedTourStepDef[];
};

export const GUIDED_TOUR_TOPICS: GuidedTourTopicDef[] = [
  {
    id: 'chats',
    title: 'צ׳אטים והודעות',
    description: 'רשימת שיחות, חלון שיחה ואזור שליחה',
    steps: [
      {
        id: 'chats-list',
        path: '/chats',
        target: '[data-tour="chat-users-list"]',
        title: 'רשימת השיחות',
        content: 'כאן מוצגים אנשי הקשר והשיחות הפעילות. בוחרים שיחה כדי לפתוח אותה בחלון ליד.',
        placement: 'right',
        spotlightPadding: 6,
      },
      {
        id: 'chats-conversation',
        path: '/chats',
        target: '[data-tour="chat-conversation-panel"]',
        title: 'חלון השיחה',
        content: 'ההודעות מוצגות כאן לפי סדר זמן. ניתן לגלול, לחפש ולנהל את השיחה מהכותרת.',
        placement: 'left',
        spotlightPadding: 6,
        joyrideFixed: true,
      },
      {
        id: 'chats-send',
        path: '/chats',
        target: '[data-tour="chat-send-message"]',
        title: 'שליחת הודעה',
        content: 'מקלידים טקסט ושולחים. בהתאם להגדרות — תבניות, קבצים וכלים נוספים מופיעים ליד אזור הכתיבה.',
        placement: 'top',
        spotlightPadding: 4,
        joyrideFixed: true,
      },
    ],
  },
  {
    id: 'whatsapp',
    title: 'חיבור וואטסאפ',
    description: 'מיקום כפתור החיבור בהדר',
    steps: [
      {
        id: 'wa-context',
        path: '/chats',
        target: '[data-tour="chat-users-list"]',
        title: 'לפני החיבור',
        content:
          'מומלץ לעבוד מתוך מסך הצ׳אטים. חיבור WhatsApp Business (Embedded Signup) מתבצע מדף ההגדרות בחברה — פייסבוק / מטא (תפריט חברה). לאחר חיבור המספר — השיחות יופיעו ברשימה.',
        placement: 'right',
      },
    ],
  },
  {
    id: 'company',
    title: 'הגדרות חברה',
    description: 'פרטי עסק ומזהים',
    steps: [
      {
        id: 'company-main',
        path: '/account/about',
        target: '[data-tour="company-about-main"]',
        title: 'פרטי החברה',
        content: 'כאן מוצגים מספר וואטסאפ, מזהה חברה ופרטים בסיסיים. חלק מהשדות לקריאה בלבד.',
        placement: 'center',
      },
    ],
  },
  {
    id: 'users',
    title: 'משתמשי מערכת',
    description: 'ניהול משתמשים והזמנות',
    steps: [
      {
        id: 'users-main',
        path: '/users',
        target: '[data-tour="app-users-page"]',
        title: 'משתמשים',
        content: 'טבלת המשתמשים הפעילים וההזמנות הממתינות. ניתן להזמין משתמשים חדשים ולנהל הרשאות.',
        placement: 'center',
      },
    ],
  },
  {
    id: 'contacts',
    title: 'אנשי קשר',
    description: 'רשימת אנשי קשר לצ׳אט',
    steps: [
      {
        id: 'contacts-main',
        path: '/contacts/chat',
        target: '[data-tour="contacts-chat-page"]',
        title: 'אנשי קשר',
        content: 'כאן מנהלים אנשי קשר, סטטוסים ופעולות המוניות. אפשר לייבא מקובץ או ליצור איש קשר חדש.',
        placement: 'center',
      },
    ],
  },
  {
    id: 'templates',
    title: 'תבניות הודעות',
    description: 'יצירת תבנית לוואטסאפ',
    steps: [
      {
        id: 'templates-main',
        path: '/templates/create',
        target: '[data-tour="template-editor-card"]',
        title: 'עורך תבנית',
        content: 'מגדירים שם, קטגוריה, גוף הודעה, כותרת/מדיה וכפתורים לפי דרישות וואטסאפ. התצוגה המקדימה עוזרת לוודא לפני הגשה.',
        placement: 'center',
      },
    ],
  },
  {
    id: 'info-center',
    title: 'מרכז מידע',
    description: 'מדריכים, סטטוסים ושגיאות',
    steps: [
      {
        id: 'info-main',
        path: '/info-center',
        target: '[data-tour="info-center-main"]',
        title: 'מרכז המידע',
        content: 'מסמכים מסודרים לפי נושאים: וואטסאפ רשמי, תבניות, סטטוסי הודעות ושגיאות, ועוד.',
        placement: 'center',
      },
    ],
  },
];

export function getGuidedTourTopic(id: GuidedTourId): GuidedTourTopicDef | undefined {
  return GUIDED_TOUR_TOPICS.find((t) => t.id === id);
}

export function buildStepHref(path: string, search?: string): string {
  return `${path}${search ?? ''}`;
}

/** התאמת נתיב נוכחי לצעד (כולל תתי-נתיבים לדפים דינמיים) */
export function locationMatchesStep(
  pathname: string,
  search: string,
  step: Pick<GuidedTourStepDef, 'path' | 'search'>,
): boolean {
  const normalizedPath = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  const stepPath = step.path.endsWith('/') && step.path.length > 1 ? step.path.slice(0, -1) : step.path;

  const pathOk = normalizedPath === stepPath || normalizedPath.startsWith(`${stepPath}/`);

  if (!pathOk) return false;

  if (!step.search) return true;

  const want = new URLSearchParams(step.search.startsWith('?') ? step.search.slice(1) : step.search);
  const have = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  for (const [k, v] of want.entries()) {
    if (have.get(k) !== v) return false;
  }
  return true;
}
